/**
 * RUM consent gate + consent-epoch invalidation counter.
 *
 * Shared between the lazily-loaded drain (`~/lib/analytics/rum-drain`) and the
 * always-loaded emitter (`~/lib/gtag`'s pre-bind queue), which is exactly why
 * it lives OUTSIDE the drain chunk: the pre-bind queue exists because the
 * drain chunk hasn't resolved yet, so the queue can't read an epoch owned by
 * the drain. This module is tiny (a counter + the sync consent gate) and only
 * pulls modules the main analytics bundle already ships (`consent`,
 * `hydrate-region-cookie`, `region`); in RUM-disabled builds every call site
 * is inside a dead `NEXT_PUBLIC_RUM_ENABLED === 'true'` branch, so the import
 * tree-shakes away with the rest.
 *
 * THE CONSENT-EPOCH INVARIANT (r3543582085 + r3543582090 — closes the class
 * structurally, subsuming the earlier per-branch buffer clears):
 *
 *   A RUM sample may only ever transmit under the SAME consent epoch it was
 *   captured in. The epoch increments on every event that invalidates
 *   previously-captured data:
 *
 *     1. RUM grantedness FLIPS in either direction (a consent withdrawal, or
 *        an explicit opt-in in a consent-required region) — observed via
 *        `CONSENT_CHANGED_EVENT`, which the consent module emits for same-tab
 *        changes (`notifyConsentChanged`) AND re-emits from its cross-tab
 *        `storage` listener (r3547054576), so a toggle made in another tab
 *        invalidates this tab's buffered samples exactly like a local one.
 *        A flip TO not-granted invalidates samples
 *        captured under the withdrawn consent even if it is re-granted before
 *        the next flush; a flip TO granted invalidates samples captured
 *        pre-opt-in (the gtag pre-bind queue captures unconditionally, so
 *        without this a pre-opt-in `resource_view` would replay — and
 *        transmit — under the later opt-in).
 *     2. Region hydration resolves CONSENT-REQUIRED (blocked Set-Cookie hop) —
 *        observed lazily: the hydrated signal is a one-way latch, so an
 *        epoch read edge-detects it without subscribing to the fetch. This
 *        preserves the r3541077076/r3541422292 guarantee ("nothing captured
 *        before the region was known can leave the page") and extends it to
 *        the pre-bind queue, which no drain-side clear could reach.
 *
 *   Same-state consent changes (e.g. a rest-of-world visitor clicking
 *   "Accept" while already default-allowed) do NOT bump: samples captured
 *   under an unchanged grant stay valid — dropping them would reduce
 *   first-party collection beyond the sitewide posture
 *   (docs/analytics/first-party-analytics-posture.md).
 */

import {
  CONSENT_CHANGED_EVENT,
  hasExplicitAnalyticsOptIn,
  isAnalyticsAllowed,
} from "~/lib/analytics/consent"
import { getHydratedConsentRequiredSignal } from "~/lib/analytics/hydrate-region-cookie"
import { REGION_CONSENT_REQUIRED_COOKIE } from "~/lib/analytics/region"

/** True in an EEA/UK/CH default-deny region, read from the edge middleware's
 *  region cookie with no network round-trip (mirrors the attribution gate). */
export function isConsentRequiredRegionClient(): boolean {
  if (typeof document === "undefined") return false
  try {
    return document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`)
  } catch {
    return false
  }
}

/** Whether the middleware's region cookie is present AT ALL (either value).
 *  Absent = a cacheable hop / blocked Set-Cookie — the gap the shared
 *  `hydrate-region-cookie` layer exists to close (r3540576136). */
export function hasRegionCookieClient(): boolean {
  if (typeof document === "undefined") return false
  try {
    const header = document.cookie
    return (
      header.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) ||
      header.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=0`)
    )
  } catch {
    return false
  }
}

/**
 * Whether first-party RUM may be collected right now. This drain bypasses Google
 * Consent Mode (it posts to our OWN endpoint), so — exactly like the attribution
 * storage gate — an EEA/UK/CH visitor needs an EXPLICIT opt-in, not the
 * rest-of-world opt-out default that `isAnalyticsAllowed()` returns for them
 * (Consent Mode handles the Google tag for that case, but not us). (review feedback.)
 *
 * Region resolution mirrors `isAnalyticsStorageAllowedAsync`: the middleware
 * cookie when present, else the hydration helper's cached consent-required
 * signal (covers blocked Set-Cookie hops — r3540576136).
 */
export function isRumConsentGranted(): boolean {
  if (!isAnalyticsAllowed()) return false
  const consentRequired =
    isConsentRequiredRegionClient() ||
    (!hasRegionCookieClient() && getHydratedConsentRequiredSignal())
  if (consentRequired && !hasExplicitAnalyticsOptIn()) return false
  return true
}

let consentEpoch = 0
let watchersBound = false
let lastGranted = false
let sawHydratedConsentRequired = false

/**
 * Reconcile the epoch against the two invalidation sources. Called on every
 * epoch read AND on every consent-change event, so:
 *   - the hydrated consent-required signal (a one-way latch set by the shared
 *     fetch, which this module deliberately does NOT trigger or subscribe to)
 *     is edge-detected no later than the next capture/flush/replay, and
 *   - grantedness flips are caught synchronously by the event listener.
 */
function reconcileEpoch(): void {
  if (!sawHydratedConsentRequired && getHydratedConsentRequiredSignal()) {
    sawHydratedConsentRequired = true
    consentEpoch += 1
    // The signal usually flips grantedness too (consent-required without an
    // opt-in). Refresh the baseline so the NEXT real flip (e.g. the visitor
    // opting in afterwards) is still detected as a flip and bumps again.
    lastGranted = isRumConsentGranted()
    return
  }
  const granted = isRumConsentGranted()
  if (granted !== lastGranted) {
    lastGranted = granted
    consentEpoch += 1
  }
}

function ensureEpochWatchers(): void {
  if (watchersBound || typeof window === "undefined") return
  watchersBound = true
  // Baselines are taken at the FIRST epoch read — i.e. at the first capture —
  // so state that was already settled before any RUM data existed (an
  // already-resolved consent-required signal, an already-granted consent)
  // does not retroactively invalidate the first samples.
  lastGranted = isRumConsentGranted()
  sawHydratedConsentRequired = getHydratedConsentRequiredSignal()
  window.addEventListener(CONSENT_CHANGED_EVENT, reconcileEpoch)
}

/**
 * The current consent epoch. Capture sites (the drain buffers AND gtag's
 * pre-bind queue) stamp this on every item; flush/replay transmits only items
 * whose epoch still equals the current one. Reading the epoch lazily binds
 * the invalidation watchers and edge-detects the hydrated region signal, so
 * the first capture establishes the baseline and every later read observes
 * any invalidation that happened in between.
 */
export function currentRumConsentEpoch(): number {
  ensureEpochWatchers()
  reconcileEpoch()
  return consentEpoch
}
